/**
 * Router de l'app GGVie consulting (SD-3875).
 *
 * Scope : uniquement les routes de l'agent consulting GGVie + authentification.
 * Pas d'admin, pas d'éval, pas d'autres agents — c'est une SPA dédiée.
 *
 * Basename : configurable via `BASE_URL` injecté par Vite (`/` en standalone,
 * `/ggvie/` quand l'app est mountée à l'intérieur d'une sandbox).
 */
import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

import { fetchMe, onSessionExpired } from '@ggvie-consulting/auth/api'
import { useAuth } from '@ggvie-consulting/auth/useAuth'
import { useMaintenance } from '@ggvie-consulting/maintenance/useMaintenance'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@ggvie-consulting/consulting/GGVIEChatView.vue'),
      meta: { requiresAuth: true, agent: 'consulting_ggvie' },
    },
    {
      path: '/:conversationId',
      name: 'conversation',
      component: () => import('@ggvie-consulting/consulting/GGVIEChatView.vue'),
      props: true,
      meta: { requiresAuth: true, agent: 'consulting_ggvie' },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@ggvie-consulting/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      // SD-4367 — page exclusive pendant une maintenance active (non-admins).
      // Route STATIQUE : gagne sur `/:conversationId` au ranking vue-router.
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('@ggvie-consulting/maintenance/MaintenanceView.vue'),
      meta: { public: true },
    },
    {
      // SD-4544 — arrivée depuis la Trilogie Sapiendo (`/sso?token=...`).
      // Route STATIQUE et PUBLIQUE : sans cela `/:conversationId` la capturerait et le
      // guard renverrait vers /login avant même la lecture du jeton.
      path: '/sso',
      name: 'sso',
      component: () => import('@ggvie-consulting/auth/SsoView.vue'),
      meta: { public: true },
    },
    {
      path: '/request-access',
      name: 'request-access',
      component: () => import('@ggvie-consulting/auth/RequestAccessView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@ggvie-consulting/auth/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/set-password/:token',
      name: 'set-password',
      component: () => import('@ggvie-consulting/auth/SetPasswordView.vue'),
      meta: { public: true },
      props: true,
    },
    {
      path: '/forgot-password/:token',
      name: 'reset-password',
      component: () => import('@ggvie-consulting/auth/SetPasswordView.vue'),
      meta: { public: true },
      props: true,
    },
  ],
})

// SD-3875 phase 2 — quand la session expire en cours de route (refresh KO sur
// 401), on redirige automatiquement vers /login en préservant l'URL courante
// dans `?redirect=`. Évite les "Erreur de transcription (401)" cryptiques.
onSessionExpired(() => {
  const current = router.currentRoute.value
  if (current.meta.public) return
  if (current.name === 'login') return
  void router.push({
    name: 'login',
    query: { redirect: current.fullPath, reason: 'session_expired' },
  })
})

// SD-4220 — chien de garde de session. Toutes les 30 s, si on est sur une page
// authentifiée, on revérifie la session côté serveur (`fetchMe` via `authFetch`) :
// un access token expiré déclenche un refresh silencieux (session gardée vivante
// sans friction), et si le refresh échoue, `sessionExpired` est émis → redirection
// /login. Évite qu'un conseiller reste devant une page muette/des messages vides
// sans réaliser qu'il a été déconnecté.
const SESSION_CHECK_INTERVAL_MS = 30_000
setInterval(() => {
  if (router.currentRoute.value.meta.public) return
  if (!useAuth().isAuthenticated.value) return
  void fetchMe().catch(() => undefined)
}, SESSION_CHECK_INTERVAL_MS)

// SD-4367 — polling du statut maintenance (10 s, singleton) : alimente la toast de
// décompte et la bascule/restauration automatiques. Démarré au scope module, comme
// le chien de garde de session ci-dessus.
const maintenance = useMaintenance()
maintenance.startPolling()

// Bascule HORS navigation : quand le statut effectif passe à `active` (poll 10 s,
// tick local à 0:00, ou 503-marqueur), on redirige immédiatement le non-admin vers
// la page maintenance en mémorisant sa route pour la restauration.
watch(maintenance.status, (status) => {
  const current = router.currentRoute.value
  if (status === 'active' && !useAuth().isAdmin.value && !current.meta.public && current.name !== 'maintenance') {
    maintenance.saveReturnTo(current.fullPath)
    void router.push({ name: 'maintenance' })
  }
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  // SD-4367 — le guard doit connaître l'état maintenance dès le premier rendu
  // (promesse memoizée, un seul fetch au boot, ne rejette jamais).
  await maintenance.ensureLoaded()

  const auth = useAuth()

  // SD-4367 — maintenance active : un non-admin n'accède qu'à la page maintenance
  // (et aux routes publiques comme /login). Quand la maintenance est terminée, la
  // page maintenance restaure la route mémorisée.
  if (maintenance.status.value === 'active' && !auth.isAdmin.value) {
    if (to.name !== 'maintenance' && !to.meta.public) {
      maintenance.saveReturnTo(to.fullPath)
      return { name: 'maintenance' }
    }
  } else if (to.name === 'maintenance') {
    return maintenance.consumeReturnTo() ?? { name: 'home' }
  }

  const isPublic = Boolean(to.meta.public)
  const requiresAuth = Boolean(to.meta.requiresAuth)
  const requiredAgent = (to.meta.agent as string | undefined) || null

  if (isPublic) return true

  if (!auth.isAuthenticated.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  fetchMe().catch(() => undefined)

  if (requiresAuth && !auth.isAuthenticated.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (requiredAgent && !auth.isAdmin.value) {
    const agents = auth.user.value?.agents ?? []
    if (!agents.includes(requiredAgent)) {
      return { name: 'login' }
    }
  }

  return true
})
